import { useRef, useState } from "react";
import { Upload, Eye, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Field, TextInput } from "@/components/common/Forms";
import { FileThumb, FilePicker, FilePreviewDialog, DownloadButton } from "@/components/common/Files";
import { EmptyState, Loader } from "@/components/common/Loader";
import { api, errMsg, useFetch } from "@/lib/api";
import { SECTIONS, fmtDate } from "@/lib/format";

const EMPTY = { section: "attestati", title: "", specialist_id: "", protocol_id: "", doc_date: "", signed_date: "", expiry_date: "" };

function UploadDialog({ open, onOpenChange, center, protocols, onUploaded }) {
  const [f, setF] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: typeof v === "string" ? v : v.target.value });
  const submit = async () => {
    if (!files.length) return toast.error("Seleziona almeno un file");
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    files.forEach((x) => fd.append("files", x));
    try {
      await api.post(`/admin/centers/${center.id}/documents`, fd);
      toast.success("Documenti caricati. Il centro riceverà un'email.");
      setF(EMPTY);
      setFiles([]);
      onOpenChange(false);
      onUploaded();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ivory-card rounded-xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">Carica documenti</DialogTitle>
          <DialogDescription>Puoi selezionare più file insieme (PNG, SVG, PDF, JPG).</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <Field label="Sezione">
            <Select value={f.section} onValueChange={set("section")}>
              <SelectTrigger data-testid="upload-section-select" className="bg-ivory-deep rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(SECTIONS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {f.section === "attestati" && (
            <>
              <Field label="Specialist *">
                <Select value={f.specialist_id} onValueChange={set("specialist_id")}>
                  <SelectTrigger data-testid="upload-specialist-select" className="bg-ivory-deep rounded-xl h-11"><SelectValue placeholder="Scegli la specialist" /></SelectTrigger>
                  <SelectContent>{(center.specialists || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Protocollo *">
                <Select value={f.protocol_id} onValueChange={set("protocol_id")}>
                  <SelectTrigger data-testid="upload-protocol-select" className="bg-ivory-deep rounded-xl h-11"><SelectValue placeholder="Scegli il protocollo" /></SelectTrigger>
                  <SelectContent>{protocols.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Data attestato"><TextInput data-testid="upload-doc-date" type="date" value={f.doc_date} onChange={set("doc_date")} /></Field>
            </>
          )}
          {f.section === "contratti" && (
            <>
              <Field label="Titolo *"><TextInput data-testid="upload-title-input" value={f.title} onChange={set("title")} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Data firma *"><TextInput data-testid="upload-signed-date" type="date" value={f.signed_date} onChange={set("signed_date")} /></Field>
                <Field label="Scadenza"><TextInput data-testid="upload-expiry-date" type="date" value={f.expiry_date} onChange={set("expiry_date")} /></Field>
              </div>
            </>
          )}
          {f.section === "documenti" && <Field label="Titolo (facoltativo)"><TextInput data-testid="upload-title-input" value={f.title} onChange={set("title")} placeholder="Es. Listino prezzi 2026" /></Field>}
          {f.section === "loghi" && <Field label="Titolo (facoltativo)"><TextInput data-testid="upload-title-input" value={f.title} onChange={set("title")} placeholder="Es. Logo orizzontale" /></Field>}
          <FilePicker files={files} setFiles={setFiles} accept=".pdf,.png,.jpg,.jpeg,.svg,.webp" testid="upload-files" label="Scegli i file" />
          <button data-testid="upload-submit-btn" disabled={busy} onClick={submit} className="btn-gold w-full">{busy ? "Caricamento…" : "Carica"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DocAdminRow({ d, onPreview, onReplace, onDelete }) {
  const sub = d.section === "attestati" ? `${d.specialist_name} · ${d.protocol_name} · ${fmtDate(d.doc_date)}`
    : d.section === "contratti" ? `Firmato ${fmtDate(d.signed_date)}${d.expiry_date ? ` · Scade ${fmtDate(d.expiry_date)}` : ""}` : d.filename;
  return (
    <div data-testid={`admin-document-${d.id}`} className="flex items-center gap-4 py-4">
      <button onClick={() => onPreview(d)}><FileThumb kind="document" file={d} className="h-12 w-12" /></button>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">{d.title}</p>
        <p className="text-xs text-warm-text truncate">{sub} · caricato il {fmtDate(d.created_at)} da {d.uploaded_by_name}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button data-testid={`admin-doc-preview-${d.id}`} onClick={() => onPreview(d)} aria-label="Anteprima"><Eye className="w-4 h-4" strokeWidth={1.4} /></button>
        <DownloadButton kind="document" id={d.id} compact label="" testid={`admin-doc-download-${d.id}`} />
        <button data-testid={`admin-doc-replace-${d.id}`} onClick={() => onReplace(d)} aria-label="Sostituisci"><RefreshCw className="w-4 h-4" strokeWidth={1.4} /></button>
        <button data-testid={`admin-doc-delete-${d.id}`} onClick={() => onDelete(d)} aria-label="Elimina"><Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.4} /></button>
      </div>
    </div>
  );
}

export default function CenterDocumentsAdmin({ center, lists }) {
  const { data, loading, reload } = useFetch(`/admin/centers/${center.id}/documents`);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const replaceRef = useRef(null);
  const [replacing, setReplacing] = useState(null);

  const doReplace = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !replacing) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.put(`/admin/documents/${replacing.id}/file`, fd);
      toast.success("File sostituito");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/admin/documents/${toDelete.id}`);
      toast.success("Documento eliminato");
      setToDelete(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-6">
        <button data-testid="open-upload-dialog-btn" onClick={() => setOpen(true)} className="btn-gold"><Upload className="w-4 h-4" /> Carica documenti</button>
      </div>
      <input ref={replaceRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.svg,.webp" onChange={doReplace} data-testid="replace-file-input" />
      {loading && !data ? <Loader /> : !data?.length ? <EmptyState title="Nessun documento" text="Carica attestati, contratti, loghi e documenti per questo centro." /> : (
        <div className="space-y-6">
          {Object.entries(SECTIONS).map(([k, l]) => {
            const docs = data.filter((d) => d.section === k);
            return (
              <section key={k} className="lux-card px-6 py-5">
                <p className="eyebrow mb-2">{l} · {docs.length}</p>
                {docs.length === 0 ? <p className="text-sm text-warm-text py-3">Nessun file.</p> : (
                  <div className="divide-y divide-warm/30">
                    {docs.map((d) => (
                      <DocAdminRow key={d.id} d={d} onPreview={setPreview} onDelete={setToDelete}
                        onReplace={(doc) => { setReplacing(doc); replaceRef.current?.click(); }} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
      <UploadDialog open={open} onOpenChange={setOpen} center={center} protocols={lists.protocols.filter((p) => p.active)} onUploaded={reload} />
      <FilePreviewDialog kind="document" file={preview} onClose={() => setPreview(null)} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-ivory-card rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl font-normal">Eliminare il documento?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" non sarà più visibile al centro. L'operazione viene registrata nel registro attività.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-doc-btn" onClick={doDelete} className="rounded-xl bg-destructive">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
